import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  ElementRef,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { NumberPipe } from '@pipes/number.pipe';
import { getColorHex, getColorHexWithOpacity } from '@utils/color';
import { deg2Rad, Vector, vScale, vSum } from '@utils/geom';

const totalAngle = deg2Rad(230);
const endAngle = (totalAngle - Math.PI) / 2;
const startAngle = Math.PI - endAngle;
const radius = 100;
const innerCircleRadius = 90;
const center = {
  x: 100,
  y: 100,
};
const pMin = vSum(
  center,
  vScale(radius, {
    x: Math.cos(startAngle),
    y: Math.sin(startAngle),
  })
);
const pMax = vSum(
  center,
  vScale(radius, {
    x: Math.cos(endAngle),
    y: Math.sin(endAngle),
  })
);

function getProgressPoint(
  center: Vector,
  radius: number,
  progressAngle: number
): Vector {
  return vSum(
    center,
    vScale(radius, {
      x: Math.cos(progressAngle),
      y: Math.sin(progressAngle),
    })
  );
}

function getArcThresholdConfig({
  center,
  radius,
  startValue,
  endValue,
  min,
  max,
  styles,
}: {
  center: Vector;
  radius: number;
  startValue: number | undefined;
  endValue: number | undefined;
  min: number;
  max: number;
  styles: { color: string; strokeDasharray?: string };
}) {
  if (
    endValue === undefined ||
    startValue === undefined ||
    Number.isNaN(min) ||
    Number.isNaN(max)
  ) {
    return null;
  }
  const endValueCapped = Math.max(min, Math.min(endValue, max));
  const startValueCapped = Math.min(max, Math.max(startValue, min));
  const endPoint = getProgressPoint(
    center,
    radius,
    ((endValueCapped - min) / (max - min)) * totalAngle + startAngle
  );
  const isLargeAngle =
    ((endValueCapped - min - (startValueCapped - min)) / (max - min)) *
      totalAngle >
    Math.PI
      ? '1'
      : '0';
  const startPoint = getProgressPoint(
    center,
    radius,
    ((startValueCapped - min) / (max - min)) * totalAngle + startAngle
  );
  const config = {
    radius,
    endPoint,
    isLargeAngle,
    startPoint,
    ...styles,
    d: `M ${startPoint!.x} ${
      startPoint.y
    } A ${radius} ${radius} 0 ${isLargeAngle} 1 ${endPoint.x} ${endPoint.y}`,
  };
  return config;
}

function getLineThresholdConfig({
  center,
  radius,
  value,
  max,
  min,
  styles,
  previous,
}: {
  center: Vector;
  radius: number;
  value: number | undefined;
  max: number;
  min: number;
  styles: { strokeDasharray?: string };
  previous?: number | undefined;
}) {
  if (value === undefined || Number.isNaN(min) || Number.isNaN(max)) {
    return null;
  }
  const valueCapped = Math.min(max, Math.max(min, value));
  const percentage = (valueCapped - min) / (max - min);
  const previousPercentage =
    previous !== undefined
      ? (Math.min(max, Math.max(min, previous)) - min) / (max - min)
      : undefined;
  let external = false;
  if (previousPercentage && percentage - previousPercentage <= 0.03) {
    // This fix has been made to fix the overlapping thresholds in Gas Monitoring, but it is not a general solution as the anchor
    // and baseline of the text may need to change based on the percentage.
    // The difference has been kept intentionally low (<= 0.03) to avoid it influencing other widgets.
    // If this change is required in other widgets, it needs to be improved.
    external = true;
  }
  const p0 = getProgressPoint(
    center,
    radius - 10,
    percentage * totalAngle + startAngle
  );
  const p1 = getProgressPoint(
    center,
    radius + 10,
    percentage * totalAngle + startAngle
  );
  return {
    ...styles,
    p0,
    p1,
    text: {
      point: getProgressPoint(
        center,
        radius + (external ? 15 : -15),
        percentage * totalAngle + startAngle
      ),
      value: valueCapped,
      anchor:
        percentage < 1 / 3 && !external
          ? 'start'
          : percentage < 2 / 3 || external
          ? 'middle'
          : 'end',
      baseline:
        percentage < 1 / 3 || percentage >= 2 / 3 || external
          ? 'middle'
          : 'hanging',
    },
    d: `M ${p0.x} ${p0.y} L ${p1.x} ${p1.y}`,
  };
}

@Component({
  selector: 'abb-gauge',
  standalone: true,
  imports: [CommonModule, NumberPipe],
  template: ` <svg [attr.viewBox]="viewBox">
    <style>
      text {
        fill: var(--abb-color-font);
      }
    </style>
    <path
      [attr.d]="grayPath"
      [attr.stroke]="bgColor"
      [attr.stroke-width]="10"
      [attr.fill-opacity]="0"
    />
    <path
      #pathRef
      [attr.d]="filledPath()"
      [attr.stroke]="color()"
      [attr.stroke-width]="10"
      [attr.fill-opacity]="0"
    />

    @for (config of arcThresholdConfigs(); track $index) { @if (config) {
    <path
      [attr.stroke-width]="2"
      [attr.fill-opacity]="0"
      [attr.stroke]="config.color"
      [attr.stroke-dasharray]="config.strokeDasharray"
      [attr.d]="config.d"
    />
    } } @for (config of lineThresholdConfigs(); track $index) { @if (config) {
    <path
      [attr.d]="config.d"
      [attr.stroke-dasharray]="config.strokeDasharray"
      [attr.stroke-width]="2"
      [attr.stroke]="fontColor()"
    />
    @if (showThresholdLabels()) {
    <text
      [attr.x]="config.text.point.x"
      [attr.y]="config.text.point.y"
      [attr.text-anchor]="config.text.anchor"
      [attr.dominant-baseline]="config.text.baseline"
      class="text-xs"
    >
      {{ config.text.value | number }}
    </text>
    } } }
    <text
      [attr.x]="pMin.x"
      [attr.y]="pMin.y + 15"
      [attr.text-anchor]="'start'"
      [attr.dominant-baseline]="'middle'"
      class="text-xs"
    >
      {{ minLabel() ?? (min() | number) }}
    </text>
    <text
      [attr.x]="pMax.x"
      [attr.y]="pMax.y + 15"
      [attr.text-anchor]="'end'"
      [attr.dominant-baseline]="'middle'"
      class="text-xs"
    >
      {{ maxLabel() ?? (max() | number) }}
    </text>
    @if (showValueLabel()) {
    <text
      [attr.font-weight]="600"
      [attr.font-size]="30"
      [attr.x]="center.x"
      [attr.y]="center.y"
      [attr.text-anchor]="'middle'"
      [attr.dominant-baseline]="'auto'"
    >
      @if (valueLabel()) {
      {{ valueLabel() }}
      } @else if (value() !== null) {
      {{ value() | number }} {{ unit() }}
      } @else { N/A }
    </text>
    <text
    [attr.x]="center.x"
    [attr.y]="center.y + 30"
    [attr.text-anchor]="'middle'"
    [attr.dominant-baseline]="'hanging'"
    class="text-xs"
  >
    {{ metric() }}
  </text>
    }
  </svg>`,
  styles: ``,
})
export class AbbGaugeComponent {
  min = input<number>(0);
  max = input<number>(100);
  value = input<number>(0);
  minLabel = input<string>();
  maxLabel = input<string>();
  lowAlarm = input<number>();
  highAlarm = input<number>();
  lowWarn = input<number>();
  highWarn = input<number>();
  metric = input<string | null | 0>();
  unit = input('');
  valueLabel = input('');
  showValueLabel = input(true);
  showThresholdLabels = input(true);
  thresholdType = input<'arc' | 'line'>('arc');
  alarmColor = input(getColorHex('abb-color-error'));
  warningColor = input(getColorHex('abb-color-warning'));
  defaultColor = input(getColorHex('abb-color-success'));
  fontColor = computed(() => getColorHex('base-value-color-font'));

  pMax = pMax;
  pMin = pMin;
  center = center;
  bgColor = getColorHexWithOpacity('abb-color-black', 0.18);
  viewBox = `-10 -10 220 ${
    220 - (1 - Math.cos(Math.PI - totalAngle / 2)) * radius + 15
  }`;

  pathRef = viewChild<ElementRef<SVGPathElement>>('pathRef');
  progress = computed(() => {
    const value = this.value();
    const min = this.min();
    const max = this.max();
    if (value === null || Number.isNaN(min) || Number.isNaN(max)) {
      return 0;
    }
    if (max - min === 0) {
      return 0;
    }
    return Math.max(0, Math.min((value - min) / (max - min), 1));
  });
  progressAngle = computed(() => this.progress() * totalAngle + startAngle);
  prevProgressAngle = startAngle;

  progressPoint = signal<Vector>(
    getProgressPoint(center, radius, this.progressAngle())
  );
  isLargeAngle = computed(() =>
    this.progress() * totalAngle > Math.PI ? '1' : '0'
  );
  grayPath = `M ${pMin.x} ${pMin.y} A ${radius} ${radius} 0 1 1 ${pMax.x} ${pMax.y}`;

  filledPath = computed(
    () =>
      `M ${pMin.x} ${pMin.y} A ${radius} ${radius} 0 ${this.isLargeAngle()} 1 ${
        this.progressPoint().x
      } ${this.progressPoint().y}`
  );

  arcThresholdConfigs = computed(() =>
    this.thresholdType() === 'arc'
      ? [
          getArcThresholdConfig({
            center,
            endValue: this.lowAlarm(),
            max: this.max(),
            min: this.min(),
            radius: innerCircleRadius,
            startValue: this.min(),
            styles: { color: this.alarmColor() },
          }),
          getArcThresholdConfig({
            center,
            endValue: this.lowWarn(),
            max: this.max(),
            min: this.min(),
            radius: innerCircleRadius,
            startValue: this.lowAlarm() ?? this.min(),
            styles: { color: this.warningColor(), strokeDasharray: '6 2' },
          }),
          getArcThresholdConfig({
            center,
            endValue: this.highAlarm() ?? this.max(),
            max: this.max(),
            min: this.min(),
            radius: innerCircleRadius,
            startValue: this.highWarn(),
            styles: { color: this.warningColor(), strokeDasharray: '6 2' },
          }),
          getArcThresholdConfig({
            center,
            endValue: this.max(),
            max: this.max(),
            min: this.min(),
            radius: innerCircleRadius,
            startValue: this.highAlarm(),
            styles: { color: this.alarmColor() },
          }),
        ]
      : []
  );

  lineThresholdConfigs = computed(() =>
    this.thresholdType() === 'line'
      ? [
          getLineThresholdConfig({
            center,
            value: this.lowAlarm(),
            radius,
            max: this.max(),
            min: this.min(),
            styles: {},
          }),
          getLineThresholdConfig({
            center,
            value: this.lowWarn(),
            radius,
            max: this.max(),
            min: this.min(),
            styles: { strokeDasharray: '3 2' },
            previous: this.lowAlarm(),
          }),
          getLineThresholdConfig({
            center,
            value: this.highAlarm(),
            radius,
            max: this.max(),
            min: this.min(),
            styles: {},
            previous: this.highWarn(),
          }),
          getLineThresholdConfig({
            center,
            value: this.highWarn(),
            radius,
            max: this.max(),
            min: this.min(),
            styles: { strokeDasharray: '3 2' },
            previous: this.lowWarn(),
          }),
        ]
      : []
  );

  color = computed(() => {
    const value = this.value();
    const highAlarm = this.highAlarm();
    const lowAlarm = this.lowAlarm();
    const lowWarn = this.lowWarn();
    const highWarn = this.highWarn();
    const alarmColor = this.alarmColor();
    const warningColor = this.warningColor();
    const defaultColor = this.defaultColor();
    return value !== null &&
      ((highAlarm !== undefined && value > highAlarm) ||
        (lowAlarm !== undefined && value < lowAlarm))
      ? alarmColor
      : value !== null &&
        ((highWarn !== undefined && value > highWarn) ||
          (lowWarn !== undefined && value < lowWarn))
      ? warningColor
      : defaultColor;
  });

  private ease(t: number) {
    return 1 - (1 - t) ** 2;
  }

  private animate(newAngle: number) {
    const from = this.prevProgressAngle;
    const to = newAngle;
    const tStart = window.performance.now();
    const animationDurationMs = 500;
    const repeat = () =>
      requestAnimationFrame((now) => {
        const t = Math.min(1, (now - tStart) / animationDurationMs);
        const pathRef = this.pathRef()?.nativeElement;
        if (pathRef) {
          const currAngle = from + (to - from) * this.ease(t);
          const tempProgressPoint = getProgressPoint(center, radius, currAngle);
          pathRef.setAttribute(
            'd',
            `M ${pMin.x} ${pMin.y} A ${radius} ${radius} 0 ${
              currAngle - startAngle > Math.PI ? '1' : '0'
            } 1 ${tempProgressPoint.x} ${tempProgressPoint.y}`
          );
        }
        if (t < 1) {
          repeat();
        } else {
          this.progressPoint.set(getProgressPoint(center, radius, to));
        }
      });
    repeat();
    this.prevProgressAngle = newAngle;
  }

  constructor() {
    effect(() => {
      this.animate(this.progressAngle());
    });
  }
}
